import React from 'react'

/**
 * Textarea de HTML com syntax highlighting (sem dependências): um <pre> colorido
 * fica atrás do <textarea> com texto transparente, rolagem sincronizada.
 * Padrão de cores: tag roxa, atributo âmbar, valor verde, URL (href/src) azul,
 * comentário cinza; <img> ganha fundo destacado para achar os blocos de imagem
 * (placeholders) de longe, e <a>/link ganham cor própria.
 */

const LIGHT = {
  tag: '#7c3aed', attr: '#b45309', str: '#15803d', url: '#0369a1',
  comment: '#9ca3af', link: '#0e7490', imgBg: 'rgba(244,63,94,0.10)', imgTag: '#be123c',
  text: '#111827',
}
const DARK = {
  tag: '#c084fc', attr: '#fbbf24', str: '#4ade80', url: '#38bdf8',
  comment: '#6b7280', link: '#22d3ee', imgBg: 'rgba(244,63,94,0.16)', imgTag: '#fb7185',
  text: '#d1d5db',
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

function highlightTags(seg: string, c: typeof LIGHT) {
  return seg.replace(/(&lt;\/?)([a-zA-Z][\w-]*)((?:[^&]|&(?!lt;|gt;))*?)(\/?&gt;)/g, (m: string, open: string, name: string, attrs: string, close: string) => {
    const isImg = name.toLowerCase() === 'img'
    const isLink = name.toLowerCase() === 'a' || name.toLowerCase() === 'link'
    const nameColor = isImg ? c.imgTag : isLink ? c.link : c.tag
    const coloredAttrs = attrs.replace(
      /([a-zA-Z_:][\w:.-]*)(\s*=\s*)("[^"]*"|'[^']*')/g,
      (mm: string, an: string, eq: string, av: string) => {
        const urlish = /^(href|src|srcset|action|background)$/i.test(an)
        return `<span style="color:${c.attr}">${an}</span>${eq}<span style="color:${urlish ? c.url : c.str}">${av}</span>`
      }
    )
    const body = `<span style="color:${nameColor}">${open}${name}</span>${coloredAttrs}<span style="color:${nameColor}">${close}</span>`
    return isImg ? `<span style="background:${c.imgBg};border-radius:3px">${body}</span>` : body
  })
}

export function highlightHtml(src: string, dark = false) {
  const c = dark ? DARK : LIGHT
  const escaped = escapeHtml(src || '')
  // Comentários primeiro (isolados para o regex de tag não colorir dentro deles)
  const parts = escaped.split(/(&lt;!--[\s\S]*?--&gt;)/g)
  return parts
    .map((p, i) =>
      i % 2 === 1
        ? `<span style="color:${c.comment};font-style:italic">${p}</span>`
        : highlightTags(p, c)
    )
    .join('')
}

interface Props { value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; placeholder?: string; height?: string | number; className?: string; dark?: boolean; style?: React.CSSProperties }

export default function HtmlCodeArea({ value, onChange, placeholder, height, className = '', dark = false, style = {} }: Props) {
  const c = dark ? DARK : LIGHT

  // Fonte/espçamento IDÊNTICOS nas duas camadas — qualquer diferença desalinha o caret
  const shared = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: '12px',
    lineHeight: '1.625',
    padding: '12px 16px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    tabSize: 2,
  }

  // Uma ÚNICA área de rolagem (o wrapper), com o textarea crescendo junto do
  // conteúdo. Antes o textarea rolava por dentro e o <pre> colorido era
  // sincronizado via evento: com barra de rolagem visível as larguras das duas
  // camadas divergiam, as quebras de linha caíam em colunas diferentes e o
  // clique/seleção acertava outro trecho ("hitbox errada" — caso Turista FC).
  return (
    <div className={className} style={{ height, overflow: 'auto', overscrollBehavior: 'contain', ...style }}>
      <div style={{ position: 'relative', minHeight: '100%' }}>
        <pre
          aria-hidden="true"
          style={{ ...shared, margin: 0, color: c.text, minHeight: '100%' }}
          dangerouslySetInnerHTML={{ __html: highlightHtml(value || '', dark) + '\n' }}
        />
        <textarea
          value={value || ''}
          onChange={onChange}
          spellCheck={false}
          placeholder={placeholder}
          style={{
            ...shared,
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            resize: 'none',
            background: 'transparent',
            // Sem valor, a cor vale só para o placeholder (o texto real fica transparente
            // para o <pre> colorido aparecer por baixo)
            color: value ? 'transparent' : c.comment,
            caretColor: c.text,
            border: 0,
            outline: 'none',
            overflow: 'hidden',
          }}
        />
      </div>
    </div>
  )
}
